import { useEffect, useState } from 'react';
import type { PassportApplication } from '@unanet/unafy';
import type { LoadingState } from '#/common';
import {
  getCachedPassportAppsForUser,
  setCachedPassportAppsForUser,
} from './cache';
import { adjustApps, envAppsMatchUserApps } from './utils';

export const useAppLauncher = ({
  issuer,
  userApps,
  userKey,
}: {
  issuer?: string;
  userApps?: string[];
  userKey?: number;
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [apps, setApps] = useState<PassportApplication[] | null>(null);

  useEffect(
    function loadPassportApps() {
      const load = async (appsUrl: URL, userApps: string[]) => {
        try {
          setLoadingState('loading');
          const response = await fetch(appsUrl);
          if (!response.ok) {
            setLoadingState('failed');
          } else {
            setLoadingState('succeeded');
            const envApps: PassportApplication[] = await response.json();
            if (userKey) {
              setCachedPassportAppsForUser({ apps: envApps, userKey });
              setApps(adjustApps(envApps, userApps));
            }
          }
        } catch (e) {
          setLoadingState('failed');
        }
      };

      if (userApps && issuer) {
        const envApps = getCachedPassportAppsForUser(userKey);
        if (envApps && envAppsMatchUserApps(envApps, userApps)) {
          setApps(adjustApps(envApps, userApps));
        } else {
          const baseUrl = new URL(issuer).origin;
          const appsUrl = new URL('v1/passport/applications', baseUrl);
          load(appsUrl, userApps);
        }
      }
    },
    [userApps?.join('|'), issuer, userKey]
  );

  return { apps, loadingState };
};
